import { useCallback, useEffect, useMemo, MutableRefObject } from 'react';
import { Observable, EMPTY, of, Subject } from 'rxjs';
import {
	filter,
	catchError,
	switchMap,
	share,
	timeout,
	tap,
	takeUntil,
} from 'rxjs/operators';
import {
	DlobWebsocketClient,
	MarketId,
	ProcessedMarketData,
	ENUM_UTILS,
} from '@velocity-exchange/common';
import useOrderbookDisplayStore, {
	OrderbookSource,
	getBestAvailableOrderbookSource,
} from 'src/stores/orderbook/useOrderbookDisplayStore';
import { dlog } from 'src/dev';
import { useUncrossOrderbook } from 'src/hooks/dlob/useUncrossOrderbook';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';
import useDevStore from 'src/stores/useDevStore';
import { WS_TIMEOUT_MS, FALLBACK_COOLDOWN_MS } from '../constants';
import {
	transformMarketData,
	TransformedMarketData,
} from '../utils/transformMarketData';

interface UseOrderbookDataPipelineParams {
	client: DlobWebsocketClient;
	lastFallbackTimestamp: MutableRefObject<number>;
	subscriptionRestartKey: number;
}

interface UseOrderbookDataPipelineReturn {
	createDataPipeline: (marketId: MarketId) => Observable<TransformedMarketData>;
	destroyCurrentPipeline$: Subject<void>;
}

/**
 * Hook that creates the RxJS data pipeline for transforming websocket data.
 * Manages the destroyCurrentPipeline$ Subject for cleanup signaling.
 *
 * FE-4066: destroyCurrentPipeline$ is recreated when subscriptionRestartKey changes.
 * This means any effect that depends on destroyCurrentPipeline$ will re-run on recovery.
 * useTabVisibilityAndRecovery does not depend on this Subject directly to avoid race
 * conditions during recovery. See useTabVisibilityAndRecovery JSDoc for details.
 */
export function useOrderbookDataPipeline({
	client,
	lastFallbackTimestamp,
	subscriptionRestartKey,
}: UseOrderbookDataPipelineParams): UseOrderbookDataPipelineReturn {
	const setOrderbookStore = useOrderbookDisplayStore((s) => s.set);
	const { uncrossL2OrderbookWithOracle } = useUncrossOrderbook();

	const uncrossDisabled = useDevStore((s) => s.dlobSettings.disableUncross);
	const uncrossDisabledRef = useSyncedRef(uncrossDisabled);

	/**
	 * Subject to signal pipeline destruction.
	 * Recreated when subscriptionRestartKey changes (i.e., on recovery or tab return).
	 *
	 * This recreation is intentional - it ensures a fresh Subject for the new pipeline.
	 * However, this also means any effect depending on this Subject will re-run, which
	 * is why useTabVisibilityAndRecovery uses callbacks instead of direct dependency.
	 */
	const destroyCurrentPipeline$ = useMemo(
		() => new Subject<void>(),
		[subscriptionRestartKey]
	);

	// Cleanup destroyCurrentPipeline$ on unmount or restart
	useEffect(() => {
		return () => {
			destroyCurrentPipeline$.next();
			destroyCurrentPipeline$.complete();
		};
	}, [destroyCurrentPipeline$]);

	const createDataPipeline = useCallback(
		(marketId: MarketId): Observable<TransformedMarketData> => {
			if (!marketId) return EMPTY;

			const baseStream = client.getMarketDataStream([marketId]).pipe(
				filter(
					(data: ProcessedMarketData) => data.marketId.key === marketId.key
				),
				// Dev tool: check per-message so the flag can be toggled at runtime
				filter(() => {
					if (
						typeof window !== 'undefined' &&
						(window as any).DRIFT_DEV_BLOCK_WEBSOCKET_MESSAGES
					) {
						return false;
					}
					return true;
				})
			);

			return baseStream.pipe(
				switchMap((data: ProcessedMarketData) => {
					try {
						const transformed = transformMarketData(
							data,
							uncrossL2OrderbookWithOracle(
								data.deserializedData,
								data.marketId
							),
							uncrossDisabledRef.current
						);
						return of(transformed);
					} catch (error) {
						dlog('orderbook_websocket_subscription', 'Transform error', {
							error,
						});
						return EMPTY;
					}
				}),

				// Auto-switch to websocket when data flows
				tap(() => {
					setOrderbookStore((s) => {
						const timeSinceLastFallback =
							Date.now() - lastFallbackTimestamp.current;
						const cooldownComplete =
							timeSinceLastFallback > FALLBACK_COOLDOWN_MS;
						const bestSource = getBestAvailableOrderbookSource();

						if (
							!ENUM_UTILS.match(s.orderbookSource, bestSource) &&
							!s.disableAutoSourceSwitching &&
							cooldownComplete
						) {
							s.orderbookSource = bestSource;
						}
					});
				}),

				timeout({ first: WS_TIMEOUT_MS, each: WS_TIMEOUT_MS }),

				catchError((error) => {
					if (error.name === 'TimeoutError') {
						lastFallbackTimestamp.current = Date.now();
						setOrderbookStore((s) => {
							if (!s.disableAutoSourceSwitching) {
								s.orderbookSource = OrderbookSource.POLLING;
							}
							s.waitingForNewGroupingData = false;
						});
						dlog(
							'orderbook_websocket_subscription',
							'Websocket timeout - switching to polling fallback',
							{
								marketId: marketId.key,
								timeoutDurationMs: WS_TIMEOUT_MS,
							}
						);
					} else {
						setOrderbookStore((s) => {
							s.waitingForNewGroupingData = false;
						});
						dlog('orderbook_websocket_subscription', 'Pipeline error', {
							error,
							marketId: marketId.key,
						});
					}
					return EMPTY;
				}),

				takeUntil(destroyCurrentPipeline$),
				share()
			);
		},
		[
			client,
			setOrderbookStore,
			uncrossL2OrderbookWithOracle,
			destroyCurrentPipeline$,
			lastFallbackTimestamp,
		]
	);

	return {
		createDataPipeline,
		destroyCurrentPipeline$,
	};
}
